import React from "react";
import "./ErrorHandler.css";
import TelegramText from "../Text/TelegramText";
import TelegramButton from "../Button/TelegramButton";
import TelegramScreen from "../Screen/TelegramScreen";
import TelegramHeader from "../Header/TelegramHeader";
import { useNavigate } from "react-router-dom";
import { PATH_HOME } from "../../../constants/Paths";

const ErrorHandler = ({ 
  error, 
  title = "Something went wrong", 
  subtitle = "An unexpected error occurred",
  showReturnButton = true,
  onRetry,
  customReturnPath
}) => {
  const navigate = useNavigate();

  const handleReturnToAgent = () => {
    // Clear any problematic state
    try {
      // Clear Redux state if needed
      localStorage.removeItem('redux-state');
      
      // Navigate to home/agent page
      navigate(customReturnPath || PATH_HOME, { replace: true });
    } catch (navError) {
      console.error('Error navigating back to agent page:', navError);
      // Force reload as last resort
      window.location.href = '/';
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      // Default retry: reload current page
      window.location.reload();
    }
  };

  // Get error details for display
  const getErrorDetails = () => {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.error) return error.error;
    if (error?.details) return error.details;
    return 'An unknown error occurred';
  };

  const getErrorCode = () => {
    if (error?.status) return error.status;
    if (error?.code) return error.code;
    if (error?.statusCode) return error.statusCode;
    return null;
  };

  return (
    <TelegramScreen className="error-handler-container">
      <TelegramHeader>
        <TelegramText className="telegramTitle">{title}</TelegramText>
        <TelegramText className="telegramSubtitle">{subtitle}</TelegramText>
      </TelegramHeader>

      <div className="error-details-container">
        <div className="error-icon">⚠️</div>
        
        <div className="error-message-container">
          <TelegramText className="error-message">
            {getErrorDetails()}
          </TelegramText>
          
          {getErrorCode() && (
            <TelegramText className="error-code">
              Error Code: {getErrorCode()}
            </TelegramText>
          )}
        </div>

        <div className="error-actions-container">
          {onRetry && (
            <TelegramButton 
              className="retry-button"
              onClick={handleRetry}
            >
              Try Again
            </TelegramButton>
          )}
          
          {showReturnButton && (
            <TelegramButton 
              className="return-agent-button"
              onClick={handleReturnToAgent}
            >
              Return to Agent Page
            </TelegramButton>
          )}
        </div>

        {/* Debug info in development */}
        {process.env.NODE_ENV === 'development' && error && (
          <details className="error-debug-details">
            <summary>Debug Information</summary>
            <pre className="error-stack">
              {JSON.stringify(error, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </TelegramScreen>
  );
};

export default ErrorHandler;